// src/components/ProfileTab.jsx
import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const ProfileTab = ({ onSuccess, onError }) => {
    const { user, updateUserProfile } = useAuth();

    // Personal information
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');

    // Address information
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [country, setCountry] = useState('United States');

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Phone number formatting function
    const formatPhoneNumber = (value) => {
        const phoneNumber = value.replace(/\D/g, '');

        if (phoneNumber.length <= 3) {
            return phoneNumber;
        } else if (phoneNumber.length <= 6) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
        }
    };

    // Handle phone number input change
    const handlePhoneChange = (e) => {
        const input = e.target.value;
        const formatted = formatPhoneNumber(input);
        setPhone(formatted);
    };

    // Load user profile data when component mounts
    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
            const existingPhone = user.phone || '';
            setPhone(existingPhone ? formatPhoneNumber(existingPhone) : '');
            setStreet(user.address?.street || '');
            setCity(user.address?.city || '');
            setState(user.address?.state || '');
            setZipCode(user.address?.zipCode || '');
            setCountry(user.address?.country || 'United States');
        }
    }, [user]);

    // Clear success message after 5 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess('');
                if (onSuccess) onSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, onSuccess]);

    // Propagate errors to parent
    useEffect(() => {
        if (error && onError) {
            onError(error);
        }
    }, [error, onError]);

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        try {
            // Handle Firestore timestamp
            if (timestamp.seconds) {
                return new Date(timestamp.seconds * 1000).toLocaleDateString();
            }
            // Handle regular date
            return new Date(timestamp).toLocaleDateString();
        } catch (error) {
            return 'N/A';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!firstName.trim() || !lastName.trim()) {
            const errorMsg = 'First name and last name are required';
            setError(errorMsg);
            if (onError) onError(errorMsg);
            return;
        }

        if (phone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(phone)) {
            const errorMsg = 'Please enter a valid phone number in format (555) 123-4567';
            setError(errorMsg);
            if (onError) onError(errorMsg);
            return;
        }

        try {
            setError('');
            setSuccess('');
            setLoading(true);

            const profileData = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: phone.trim(),
                address: {
                    street: street.trim(),
                    city: city.trim(),
                    state: state.trim(),
                    zipCode: zipCode.trim(),
                    country: country.trim()
                },
                profileComplete: true
            };

            await updateUserProfile(profileData);
            const successMsg = 'Profile updated successfully!';
            setSuccess(successMsg);
            if (onSuccess) onSuccess(successMsg);
            setIsEditing(false);
        } catch (err) {
            const errorMsg = 'Failed to update profile: ' + err.message;
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        setError('');
        setSuccess('');
        if (onError) onError('');
        if (onSuccess) onSuccess('');
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError('');
        setSuccess('');
        if (onError) onError('');
        if (onSuccess) onSuccess('');

        // Reset form to original values
        if (user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
            const existingPhone = user.phone || '';
            setPhone(existingPhone ? formatPhoneNumber(existingPhone) : '');
            setStreet(user.address?.street || '');
            setCity(user.address?.city || '');
            setState(user.address?.state || '');
            setZipCode(user.address?.zipCode || '');
            setCountry(user.address?.country || 'United States');
        }
    };

    if (!user) {
        return <div>Please log in to view your profile.</div>;
    }

    return (
        <>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {/* Account Information (Read-only) */}
            <div className="mb-4 user-profile">
                <h5 className="mb-3 text-primary">Account Information</h5>
                <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <strong>Group:</strong> {user.groupName}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <strong>Role:</strong> {user.isGroupAdmin ?
                            <Badge bg="primary">Group Admin</Badge> :
                            <Badge bg="secondary">Member</Badge>
                        }
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <strong>Email:</strong> {user.email}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <strong>Member Since:</strong>{' '}
                            {formatDate(user?.createdAt)}
                        </div>
                    </Col>
                </Row>
            </div>

            <Form onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <div className="mb-4">
                    <h5 className="mb-3 text-primary">Personal Information</h5>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>First Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    disabled={!isEditing}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Last Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    disabled={!isEditing}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                            type="tel"
                            value={phone}
                            onChange={handlePhoneChange}
                            placeholder="(555) 123-4567"
                            disabled={!isEditing}
                            maxLength="14"
                        />
                    </Form.Group>
                </div>

                {/* Address Information Section */}
                <div className="mb-4">
                    <h5 className="mb-3 text-primary">Address Information</h5>
                    <Form.Group className="mb-3">
                        <Form.Label>Street Address</Form.Label>
                        <Form.Control
                            type="text"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            placeholder="123 Main Street"
                            disabled={!isEditing}
                        />
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>City</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    disabled={!isEditing}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>State</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                    placeholder="CA"
                                    disabled={!isEditing}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Zip Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={zipCode}
                                    onChange={(e) => setZipCode(e.target.value)}
                                    placeholder="12345"
                                    disabled={!isEditing}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Select
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            disabled={!isEditing}
                        >
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Australia">Australia</option>
                            <option value="Germany">Germany</option>
                            <option value="France">France</option>
                            <option value="Other">Other</option>
                        </Form.Select>
                    </Form.Group>
                </div>

                {/* Action Buttons */}
                {isEditing && (
                    <div className="d-flex justify-content-center gap-3">
                        <Button
                            variant="success"
                            type="submit"
                            disabled={loading}
                            style={{ minWidth: '120px' }}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={loading}
                            style={{ minWidth: '120px' }}
                        >
                            Cancel
                        </Button>
                    </div>
                )}
            </Form>

            {/* Edit Button (exposed for parent component to control) */}
            {!isEditing && (
                <div className="d-none">
                    <Button
                        id="profile-edit-btn"
                        variant="primary"
                        onClick={handleEdit}
                    >
                        Edit Profile
                    </Button>
                </div>
            )}
        </>
    );
};

export default ProfileTab;